/* eslint-disable react/require-default-props */
import {
  css,
  flipClock,
  theme,
  elapsedTime,
} from 'flipclock';
import PropTypes from 'prop-types';
import React, {
  useEffect, useState, useRef, useCallback,
} from 'react';

import { HtmlContentLoader, WysiwygStyleWrapper } from '@powdr/components';

import {
  ClockTitle, ClockParts, ClockPart, StyledCountdownClock,
  Value,
  Label,
  Button,
} from './styles';

export const CountdownClock = ({
  className = null,
  title = null,
  copy = null,
  countdownEndTimestamp,
  backgroundColor = null,
  countdownFinishedMessage = null,
  flipClockVersion = false,
  parentColorProfile = null,
  relationships = null,
}) => {
  const timestamp = new Date(countdownEndTimestamp).getTime();
  const [timeLeft, setTimeLeft] = useState(timestamp - Date.now());
  const [hasEnded, setHasEnded] = useState(false);

  const clockRef = useRef();
  const flipClockInstance = useRef(null); // store FlipClock instance

  const getTimeParts = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      days,
      hours,
      minutes,
      seconds,
    };
  };

  // Countdown updater
  const updateCountdown = useCallback(() => {
    const now = Date.now();
    const remaining = timestamp - now;

    if (remaining <= 0) {
      setTimeLeft(0);
      setHasEnded(true);
      return false;
    }
    setTimeLeft(remaining);
    return true;
  }, [timestamp]);

  // Tick loop
  useEffect(() => {
    updateCountdown();
    const interval = setInterval(() => {
      const keepGoing = updateCountdown();
      if (!keepGoing) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [updateCountdown]);

  useEffect(() => {
    if (!flipClockVersion || !clockRef.current) return;

    const node = clockRef.current; // capture ref in local variable

    flipClockInstance.current = flipClock({
      parent: node,
      face: elapsedTime({
        to: new Date(countdownEndTimestamp),
        format: '[DD]:[hh]:[mm]:[ss]',
      }),
      theme: theme({
        dividers: ':',
        labels: [['Days'], ['Hours'], ['Minutes'], ['Seconds']],
        css: css({}),
      }),
    });

    flipClockInstance.current.start();

    // eslint-disable-next-line consistent-return
    return () => {
      if (flipClockInstance.current) {
        flipClockInstance.current.stop();
        flipClockInstance.current = null;
      }
      // clear DOM of the captured node, not the ref directly
      if (node) node.innerHTML = '';
    };
  }, [flipClockVersion, countdownEndTimestamp]);

  return (
    <StyledCountdownClock
      className={className}
      $backgroundColor={backgroundColor}
      $colorProfile={parentColorProfile}
      $flipClockVersion={flipClockVersion}
    >
      <WysiwygStyleWrapper>
        {(title) && (
          <ClockTitle
            $colorProfile={parentColorProfile}
          >
            {title}
          </ClockTitle>
        )}

        {hasEnded ? (
          <HtmlContentLoader html={countdownFinishedMessage?.value} />
        ) : (
          <>
            {(copy?.value) && <HtmlContentLoader html={copy?.value} />}
            <ClockParts ref={clockRef} $colorProfile={parentColorProfile}>
              {!flipClockVersion && (
              <>
                <ClockPart>
                  <Value>{String(getTimeParts(timeLeft).days).padStart(2, '0')}</Value>
                  <Label>Days</Label>
                </ClockPart>
                <ClockPart>
                  <Value>{String(getTimeParts(timeLeft).hours).padStart(2, '0')}</Value>
                  <Label>Hours</Label>
                </ClockPart>
                <ClockPart>
                  <Value>{String(getTimeParts(timeLeft).minutes).padStart(2, '0')}</Value>
                  <Label>Minutes</Label>
                </ClockPart>
                <ClockPart>
                  <Value>{String(getTimeParts(timeLeft).seconds).padStart(2, '0')}</Value>
                  <Label>Seconds</Label>
                </ClockPart>
              </>
              )}
            </ClockParts>
          </>
        )}
      </WysiwygStyleWrapper>
      {relationships?.button?.buttonLink && (
      <Button
        href={relationships?.button?.buttonLink}
        target={relationships?.button?.buttonTarget}
        $colorProfile={parentColorProfile}
      >
        {relationships?.button?.buttonText}
      </Button>
      )}
    </StyledCountdownClock>
  );
};

CountdownClock.propTypes = {
  className: PropTypes.string,
  parentColorProfile: PropTypes.string,
  countdownEndTimestamp: PropTypes.string.isRequired,
  title: PropTypes.string,
  copy: PropTypes.string,
  backgroundColor: PropTypes.string,
  countdownFinishedMessage: PropTypes.shape({
    value: PropTypes.string,
  }),
  flipClockVersion: PropTypes.bool,
  relationships: PropTypes.shape({}),
};
